import clsx from 'clsx'
import { ReactNode } from 'react'

export type ProgressBarLabel = 'none' | 'right' | 'bottom' | 'top-floating' | 'bottom-floating'

export interface ProgressBarProps {
    value: number
    min?: number
    max?: number
    label?: ProgressBarLabel
    formatLabel?: (percent: number) => ReactNode
    'aria-label'?: string
    id?: string
    className?: string
}

const ROOT_LAYOUT: Record<ProgressBarLabel, string> = {
    none: '',
    right: 'flex items-center gap-spacing-lg',
    bottom: 'flex flex-col items-end gap-spacing-md',
    'top-floating': 'pt-[42px]',
    'bottom-floating': 'pb-spacing-5xl',
}

const FLOATING_POSITION: Record<'top-floating' | 'bottom-floating', string> = {
    'top-floating': 'bottom-full mb-2.5',
    'bottom-floating': 'top-full mt-spacing-md',
}

const defaultFormatLabel = (percent: number): ReactNode => `${Math.round(percent)}%`

export const ProgressBar = ({
    value,
    min = 0,
    max = 100,
    label = 'none',
    formatLabel = defaultFormatLabel,
    'aria-label': ariaLabel,
    id,
    className,
}: ProgressBarProps) => {
    const clampedValue = Math.max(min, Math.min(max, value))
    const percent = max === min ? 0 : ((clampedValue - min) / (max - min)) * 100
    const formattedLabel = formatLabel(percent)
    const valueText =
        typeof formattedLabel === 'string' || typeof formattedLabel === 'number'
            ? String(formattedLabel)
            : undefined

    const renderFloatingChip = () => {
        if (label === 'top-floating' || label === 'bottom-floating') {
            return (
                <div
                    className={clsx(
                        'pointer-events-none absolute -translate-x-1/2 whitespace-nowrap rounded-radius-md bg-background-bg-primary px-spacing-lg py-spacing-md',
                        'text-xs font-semibold text-text-secondary shadow-lg ring-1 ring-alpha-black-10',
                        FLOATING_POSITION[label],
                    )}
                    style={{ left: `${percent}%` }}>
                    {formattedLabel}
                </div>
            )
        }
        return null
    }

    const showInlineLabel = label === 'right' || label === 'bottom'

    return (
        <div
            role="progressbar"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={clampedValue}
            aria-valuetext={valueText}
            aria-label={ariaLabel}
            id={id}
            className={clsx('w-full font-body', ROOT_LAYOUT[label], className)}>
            <div className="relative w-full min-w-0 flex-1">
                <div className="h-2 w-full overflow-hidden rounded-radius-full bg-background-bg-quaternary">
                    <div
                        className="h-2 min-w-2 rounded-radius-full bg-background-bg-brand-solid transition-[width] duration-300 ease-out"
                        style={{ width: `${percent}%` }}
                    />
                </div>
                {renderFloatingChip()}
            </div>
            {showInlineLabel && (
                <span className="shrink-0 whitespace-nowrap text-sm font-medium text-text-secondary">
                    {formattedLabel}
                </span>
            )}
        </div>
    )
}
